import {
  Power,
  Gauge,
  AlertTriangle,
  MapPin,
  Activity,
  CircleDot,
  Signal,
  SignalZero,
  Moon,
  HelpCircle,
  Fuel,
  Radar,
  ShieldOff,
  Wrench,
  User,
  Image as ImageIcon,
  Terminal,
  Send,
} from "lucide-react";

// Fonte única (extraído de routes/index.tsx#TabAlertas em 2026-08-28, pra
// reaproveitar no app mobile sem duplicar/inventar tradução) — rótulos e
// ícones em PT-BR para os tipos que o Traccar realmente suporta (GET
// /api/notifications/types). Um tipo sem tradução aqui ainda é exibido —
// cai no fallback (ícone de sino + o nome cru do tipo) — nunca é ocultado
// por falta de tradução, só quando o servidor não o retorna.
export type AlertTone = "emerald" | "red" | "amber" | "sky" | "slate" | "fuchsia" | "orange";

export interface AlertTypeMeta {
  icon: typeof Power;
  label: string;
  tone: AlertTone;
}

export const ALERT_TYPE_META: Record<string, AlertTypeMeta> = {
  ignitionOn: { icon: Power, label: "Ignição ligada", tone: "emerald" },
  ignitionOff: { icon: Power, label: "Ignição desligada", tone: "slate" },
  deviceOverspeed: { icon: Gauge, label: "Excesso de velocidade", tone: "orange" },
  alarm: { icon: AlertTriangle, label: "Alarme do rastreador", tone: "red" },
  geofenceEnter: { icon: MapPin, label: "Entrada em cerca eletrônica", tone: "sky" },
  geofenceExit: { icon: MapPin, label: "Saída de cerca eletrônica", tone: "fuchsia" },
  deviceMoving: { icon: Activity, label: "Veículo em movimento", tone: "emerald" },
  deviceStopped: { icon: CircleDot, label: "Veículo parado", tone: "slate" },
  deviceOnline: { icon: Signal, label: "Dispositivo online", tone: "emerald" },
  deviceOffline: { icon: SignalZero, label: "Dispositivo offline", tone: "red" },
  deviceInactive: { icon: Moon, label: "Dispositivo inativo", tone: "slate" },
  deviceUnknown: { icon: HelpCircle, label: "Dispositivo desconhecido", tone: "slate" },
  deviceFuelDrop: { icon: Fuel, label: "Queda de combustível", tone: "red" },
  deviceFuelIncrease: { icon: Fuel, label: "Aumento de combustível", tone: "sky" },
  proximityEnter: { icon: Radar, label: "Entrada em zona de proximidade", tone: "sky" },
  proximityExit: { icon: Radar, label: "Saída de zona de proximidade", tone: "fuchsia" },
  unaccompaniedMotion: { icon: ShieldOff, label: "Movimento sem acompanhamento", tone: "red" },
  maintenance: { icon: Wrench, label: "Manutenção necessária", tone: "amber" },
  driverChanged: { icon: User, label: "Motorista alterado", tone: "sky" },
  media: { icon: ImageIcon, label: "Mídia recebida", tone: "sky" },
  commandResult: { icon: Terminal, label: "Resultado de comando", tone: "slate" },
  queuedCommandSent: { icon: Send, label: "Comando pendente enviado", tone: "slate" },
};

export const ALERT_TONE_CLASSES: Record<AlertTone, string> = {
  emerald: "text-emerald-600 border-emerald-200 bg-emerald-50",
  red: "text-red-600 border-red-200 bg-red-50",
  amber: "text-amber-600 border-amber-200 bg-amber-50",
  sky: "text-sky-600 border-sky-200 bg-sky-50",
  slate: "text-slate-600 border-slate-200 bg-slate-50",
  fuchsia: "text-fuchsia-600 border-fuchsia-200 bg-fuchsia-50",
  orange: "text-orange-600 border-orange-200 bg-orange-50",
};

// Lista curada pedida 2026-08-31: substitui a lista crua "o que o Traccar
// mandar" (que misturava commandResult/queuedCommandSent com alertas de
// verdade) por uma seleção fixa em 2 grupos, com emoji + descrição em
// PT-BR. Um item sem `traccarTypes` ainda não existe de verdade no sistema
// (nem Traccar nem nosso motor de AlertRule) — fica "Em breve" na tela do
// cliente. Quando `traccarTypes` tem mais de um valor (ex.: perímetro =
// geofenceEnter + geofenceExit), o toggle liga/desliga os dois juntos.
export type AlertGroupKey = "meus-alertas" | "avancados";

export interface AlertCatalogItem {
  key: string;
  group: AlertGroupKey;
  emoji: string;
  label: string;
  description: string;
  traccarTypes?: string[];
  // Tipo real do AlertEngine (Fase 3, push+som configurável) por trás do
  // toggle — pedido 2026-09-09: "cliente ativa a notificação, já deve
  // aparecer o alerta configurado automaticamente". Só os 5 tipos sem
  // configuração extra (sem geofence, sem janela de horário) têm isto;
  // quando presente, o toggle passa a ligar/desligar uma AlertRule de
  // verdade (POST/PUT em .../rules/quick-toggle) em vez do sistema nativo
  // do Traccar em `traccarTypes` — os dois nunca coexistem no mesmo item.
  alertRuleType?:
    | "ignition_on"
    | "ignition_off"
    | "overspeed"
    | "battery_tamper"
    | "no_signal"
    | "vibration"
    | "off_hours"
    | "vehicle_blocked"
    | "vehicle_unblocked"
    | "anti_theft"
    | "displacement";
  soundFreq: number;
  soundBeeps: 1 | 2 | 3;
}

export const ALERT_CATALOG: AlertCatalogItem[] = [
  {
    key: "ignition-on",
    group: "meus-alertas",
    emoji: "🟢",
    label: "Ignição ligada",
    description: "Avisa quando o carro é ligado",
    traccarTypes: ["ignitionOn"],
    alertRuleType: "ignition_on",
    soundFreq: 660,
    soundBeeps: 1,
  },
  {
    key: "ignition-off",
    group: "meus-alertas",
    emoji: "🔴",
    label: "Ignição desligada",
    description: "Avisa quando o carro é desligado",
    traccarTypes: ["ignitionOff"],
    alertRuleType: "ignition_off",
    soundFreq: 440,
    soundBeeps: 1,
  },
  {
    key: "battery-tamper",
    group: "meus-alertas",
    emoji: "🔋",
    label: "Bateria violada",
    description: "Avisa se mexerem na bateria",
    alertRuleType: "battery_tamper",
    soundFreq: 980,
    soundBeeps: 3,
  },
  {
    key: "lock-alert",
    group: "meus-alertas",
    emoji: "🔒",
    label: "Alerta de bloqueio",
    description: "Avisa quando o veículo é bloqueado",
    alertRuleType: "vehicle_blocked",
    soundFreq: 550,
    soundBeeps: 2,
  },
  {
    key: "unlock-alert",
    group: "meus-alertas",
    emoji: "🔓",
    label: "Alerta de desbloqueio",
    description: "Avisa quando o veículo é desbloqueado",
    alertRuleType: "vehicle_unblocked",
    soundFreq: 720,
    soundBeeps: 2,
  },
  {
    key: "antifurto",
    group: "avancados",
    emoji: "🚨",
    label: "Antifurto",
    description: "Avisa se o veículo se mover com a ignição desligada",
    alertRuleType: "anti_theft",
    soundFreq: 1046,
    soundBeeps: 3,
  },
  {
    // Pedido 2026-09-14: cliente pede pra habilitar "o alarme" sozinho em
    // Alertas Avançados — antes só existia via tela de admin "Criar
    // alerta" (Administrator/Manager). Mesmo alertRuleType "vibration" já
    // usado por lá (ver ALERT_TYPE_OPTIONS em alert-rules.service.ts,
    // reaproveita label/emoji de propósito) — é o único tipo com botões
    // de ação nativos na notificação (Ver mapa/Dispensar alarme, ver
    // GpsDriveMessagingService.java no app Android).
    key: "vibration-alert",
    group: "avancados",
    emoji: "📳",
    label: "Alerta de vibração",
    description: "Avisa quando o sensor de vibração/impacto dispara",
    alertRuleType: "vibration",
    soundFreq: 1046,
    soundBeeps: 2,
  },
  {
    key: "horario",
    group: "avancados",
    emoji: "⏰",
    label: "Horário",
    description: "Avisa se o veículo se mover fora do horário permitido (6h às 22h)",
    alertRuleType: "off_hours",
    soundFreq: 830,
    soundBeeps: 2,
  },
  // "Falha de comunicação" (no_signal, "sem sinal há mais de 30 min")
  // removido a pedido 2026-10-09 — o motor de alertas não dispara mais.
  {
    key: "speed-alert",
    group: "avancados",
    emoji: "🏎️",
    label: "Alerta de velocidade",
    description: "Quando passa do limite de velocidade",
    traccarTypes: ["deviceOverspeed"],
    alertRuleType: "overspeed",
    soundFreq: 900,
    soundBeeps: 2,
  },
  {
    key: "distance-alert",
    group: "avancados",
    emoji: "🛣️",
    label: "Alerta de deslocamento",
    description: "Avisa quando passa de 100 km rodados no dia",
    alertRuleType: "displacement",
    soundFreq: 600,
    soundBeeps: 2,
  },
  {
    key: "perimeter-alert",
    group: "avancados",
    emoji: "📍",
    label: "Alerta de perímetro",
    description: "Entrada ou saída de uma área no mapa",
    traccarTypes: ["geofenceEnter", "geofenceExit"],
    soundFreq: 760,
    soundBeeps: 1,
  },
];

// Tom de teste (Web Audio API) — som REAL de push é sempre o padrão do
// sistema operacional (navegadores não permitem customizar por tipo), então
// isto é só uma prévia audível pra identificar cada alerta, usada no toggle
// mobile e na página de admin "Notificações" (pedido 2026-08-31: dono da
// empresa poder "ver/ouvir" o som de cada notificação).
export function playAlertTone(freq: number, beeps: 1 | 2 | 3 = 1) {
  const AudioCtx =
    window.AudioContext ??
    (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
  if (!AudioCtx) return;
  const ctx = new AudioCtx();
  const beepDuration = 0.18;
  const gap = 0.12;
  for (let i = 0; i < beeps; i++) {
    const start = ctx.currentTime + i * (beepDuration + gap);
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = "sine";
    osc.frequency.value = freq;
    gain.gain.setValueAtTime(0.15, start);
    gain.gain.exponentialRampToValueAtTime(0.001, start + beepDuration);
    osc.connect(gain).connect(ctx.destination);
    osc.start(start);
    osc.stop(start + beepDuration);
  }
}
